// Move rating: one verdict per move plus one sentence of context.
//
// Pure functions only (no DOM, no store, no runtime imports) so the rules can be unit tested with
// `npm test` and reused by the practice history page.
//
// The verdict comes from the engine alone, as a loss of winning chances, so it means the same for
// every user and can be compared over time. Database statistics, the user's own games and the
// repertoire never change the verdict: they provide the insight sentence, which is most valuable
// exactly when it disagrees with the engine ("good move, but it scores 44% at your level").

import { pointsLong } from "./points.ts";
import type { TreeNode } from "./catalogTree.ts";

export type Verdict = "best" | "good" | "inaccuracy" | "mistake" | "blunder";

export const VERDICTS: Verdict[] = ["best", "good", "inaccuracy", "mistake", "blunder"];

/** What an engine reports for a position: centipawns, or mate in N. */
export interface Score {
    cp?: number | null;
    mate?: number | null;
}

/** A move inside a position, as the explorer (or a catalog tree) describes it. */
export interface ExplorerLikeMove {
    uci: string;
    san: string;
    white?: number;
    draws?: number;
    black?: number;
}

/** A move in the user's own games, as /api/player/moves returns it. */
export interface MyGamesMove {
    move: string;
    total?: number;
    wins?: number;
    draws?: number;
    losses?: number;
}

export interface DbStats {
    n: number;
    scorePct: number | null;
    sharePct: number;
    positionN: number;
    baselinePct: number;
    rank: number | null;
    topSan: string;
}

export interface MyStats {
    n: number;
    scorePct: number | null;
    positionN: number;
    mostPlayedUci: string;
    isMostPlayed: boolean;
}

export interface RepertoireContext {
    available: boolean;
    inRepertoire?: boolean;
    expectedSan?: string[];
}

export type InsightTone = "positive" | "neutral" | "warning" | "hook";

export interface Insight {
    code: string;
    tone: InsightTone;
    text: string;
}

// Verdict thresholds, in lost winning-chance points. Around equality 1 point is ~11 centipawns.
// Full-game thresholds (5/10/15) are far too lenient for openings, where a third of a pawn matters;
// 3.0 and 7.0 keep continuity with the 0.30 / 0.75 pawn limits the app used before.
const BEST_MAX_LOSS = 1.0;
const GOOD_MAX_LOSS = 3.0;
const INACCURACY_MAX_LOSS = 7.0;
const MISTAKE_MAX_LOSS = 14.0;

const CP_LIMIT = 1000;
const MATE_THRESHOLD = 20000;   // the catalog stores mate as +-(30000 - 10 * moves)
const MIN_RATED_MOVES = 3;

// Sample sizes below which a statistic is not quoted.
const MINE_MIN_GAMES = 10;
const DB_MIN_GAMES = 100;
const DB_MIN_GAMES_TO_DISAGREE = 300;
const DB_MIN_POSITION_GAMES = 1000;

const LABELS: Record<Verdict, string> = {
    best: "Best move",
    good: "Good move",
    inaccuracy: "Inaccuracy",
    mistake: "Mistake",
    blunder: "Blunder",
};

const GLYPHS: Record<Verdict, string> = { best: "★", good: "✓", inaccuracy: "?!", mistake: "?", blunder: "??" };

/** Centipawns from an engine score; a mate is a very large but ordered value. */
export function scoreToCp(score: Score | null | undefined): number | null {
    if (!score) return null;
    if (typeof score.mate === "number" && score.mate !== 0) {
        const distance = Math.min(10, Math.abs(score.mate));
        return Math.sign(score.mate) * (21 - distance) * 100;
    }
    if (typeof score.cp === "number") return score.cp;
    return null;
}

/** Winning chances (0-100) for the side the centipawns refer to. Same curve Lichess uses. */
export function winPct(cp: number): number {
    const limited = Math.abs(cp) >= MATE_THRESHOLD ? Math.sign(cp) * CP_LIMIT : Math.max(-CP_LIMIT, Math.min(CP_LIMIT, cp));
    return 50 + 50 * (2 / (1 + Math.exp(-0.00368208 * limited)) - 1);
}

/** Winning chances given away by the played move. Both values from the mover's point of view. */
export function winLoss(bestCp: number | null | undefined, playedCp: number | null | undefined): number | null {
    if (bestCp === null || bestCp === undefined || playedCp === null || playedCp === undefined) return null;
    return Math.max(0, winPct(bestCp) - winPct(playedCp));
}

export function classify(loss: number | null | undefined, { sameAsBest = false } = {}): Verdict | null {
    if (sameAsBest) return "best";
    if (loss === null || loss === undefined) return null;
    if (loss <= BEST_MAX_LOSS) return "best";
    if (loss <= GOOD_MAX_LOSS) return "good";
    if (loss <= INACCURACY_MAX_LOSS) return "inaccuracy";
    if (loss <= MISTAKE_MAX_LOSS) return "mistake";
    return "blunder";
}

export function verdictLabel(verdict: Verdict | null | undefined): string {
    return (verdict && LABELS[verdict]) || "";
}

export function verdictGlyph(verdict: Verdict | null | undefined): string {
    return (verdict && GLYPHS[verdict]) || "";
}

/**
 * Whether the move was part of your repertoire — the mark that goes on the board next to the verdict,
 * never instead of it.
 *
 * They answer different questions and a player needs both: "was that a good move" is Stockfish's job,
 * "was that *my* move" is the repertoire's, and a move can perfectly well be excellent chess and a
 * departure from what you decided to play. It says nothing at all when the repertoire does not cover
 * the position: a mark that means "no information" is noise on a board.
 *
 * @param stats the repertoire part of a move's stats, as practice records it
 */
export function repertoireMark(stats: { covers?: boolean; inRepertoire?: boolean; expectedSan?: string[] }
    | null | undefined): { state: "in" | "out"; glyph: string; title: string } | null {
    if (!stats || !stats.covers) return null;
    if (stats.inRepertoire) {
        return { state: "in", glyph: "♟", title: "This move is in your repertoire" };
    }
    const expected = (stats.expectedSan || []).filter(Boolean);
    return {
        state: "out",
        glyph: "✎",
        title: expected.length
            ? `You left your repertoire here — it plays ${expected.join(" or ")}`
            : "You left your repertoire here",
    };
}

/** True for the verdicts that keep a combo alive and count as "book quality" moves. */
export function isSolid(verdict: Verdict | null | undefined): boolean {
    return verdict === "best" || verdict === "good";
}

/** Green / yellow / red, the scale practice history and the admin charts were built on. */
export function legacyColor(verdict: Verdict | null | undefined): "green" | "yellow" | "red" | "gray" {
    if (verdict === "best" || verdict === "good") return "green";
    if (verdict === "inaccuracy") return "yellow";
    if (verdict === "mistake" || verdict === "blunder") return "red";
    return "gray";
}

/** Accuracy of one move (0-100) from its loss of winning chances. */
export function moveAccuracy(loss: number | null | undefined): number | null {
    if (loss === null || loss === undefined) return null;
    return Math.max(0, Math.min(100, 103.1668 * Math.exp(-0.04354 * loss) - 3.1669));
}

/**
 * Accuracy of a practice. Half arithmetic, half harmonic mean, so one blunder weighs more than
 * several tiny slips. Null until enough moves were rated to mean something.
 */
export function sessionAccuracy(losses: (number | null | undefined)[]): number | null {
    const accuracies = losses.filter((l) => l !== null && l !== undefined).map((l) => moveAccuracy(l)!);
    if (accuracies.length < MIN_RATED_MOVES) return null;
    const mean = accuracies.reduce((sum, a) => sum + a, 0) / accuracies.length;
    const harmonic = accuracies.length / accuracies.reduce((sum, a) => sum + 1 / Math.max(a, 1), 0);
    return Math.round(((mean + harmonic) / 2) * 10) / 10;
}

export interface CatalogRating {
    verdict: Verdict | null;
    loss: number | null;
    bestUci: string;
    bestSan: string;
    bestCp: number;
    playedCp: number;
}

/**
 * Rates a move with the evaluations stored in a catalog position.
 * Null when the move has no stored eval.
 */
export function rateFromCatalog(node: TreeNode | null | undefined, uci: string): CatalogRating | null {
    if (!node || !node.m) return null;
    const played = node.m.find((m) => m[0] === uci);
    if (!played || played[5] === null || played[5] === undefined) return null;

    // Every stored eval comes from the same search, so the best known score is the fairest reference;
    // the separate "best" search can disagree with it by a few centipawns of engine noise.
    let reference = { uci: played[0], san: played[1], cp: played[5] };
    node.m.forEach((m) => {
        if (m[5] !== null && m[5] !== undefined && m[5] > reference.cp) reference = { uci: m[0], san: m[1], cp: m[5] };
    });
    const best = node.best;
    if (best && best[2] > reference.cp && !node.m.some((m) => m[0] === best[0])) {
        reference = { uci: best[0], san: best[1], cp: best[2] };
    }

    const loss = winLoss(reference.cp, played[5]);
    return {
        verdict: classify(loss, { sameAsBest: reference.uci === uci }),
        loss, bestUci: reference.uci, bestSan: reference.san, bestCp: reference.cp, playedCp: played[5],
    };
}

/**
 * Statistics of one move inside a position, from explorer-shaped moves ({uci, san, white, draws, black}).
 * Scores are from the mover's side: win = 1, draw = 0.5.
 */
export function moveStatsFromExplorer(moves: ExplorerLikeMove[] | null | undefined, uci: string,
                                      moverColor: "white" | "black"): DbStats | null {
    if (!moves || !moves.length) return null;
    const games = (m: ExplorerLikeMove) => (m.white || 0) + (m.draws || 0) + (m.black || 0);
    const score = (m: ExplorerLikeMove) => ((moverColor === "white" ? m.white : m.black) || 0) + (m.draws || 0) / 2;
    const positionN = moves.reduce((sum, m) => sum + games(m), 0);
    if (!positionN) return null;

    const ranked = [...moves].sort((a, b) => games(b) - games(a));
    const index = ranked.findIndex((m) => m.uci === uci);
    const baselinePct = (moves.reduce((sum, m) => sum + score(m), 0) / positionN) * 100;
    const top = ranked[0]!;
    if (index < 0) {
        return { n: 0, scorePct: null, sharePct: 0, positionN, baselinePct, rank: null, topSan: top.san };
    }
    const move = ranked[index]!;
    const n = games(move);
    return {
        n, scorePct: n ? (score(move) / n) * 100 : null, sharePct: (n / positionN) * 100,
        positionN, baselinePct, rank: index + 1, topSan: top.san,
    };
}

/** Statistics of one move in the user's own games ({move: uci, total, wins, draws, losses}). */
export function moveStatsFromMyGames(moves: MyGamesMove[] | null | undefined, uci: string): MyStats | null {
    if (!moves || !moves.length) return null;
    const positionN = moves.reduce((sum, m) => sum + (m.total || 0), 0);
    if (!positionN) return null;
    const ranked = [...moves].sort((a, b) => (b.total || 0) - (a.total || 0));
    const move = moves.find((m) => m.move === uci);
    const n = move ? move.total || 0 : 0;
    return {
        n, scorePct: n && move ? (((move.wins || 0) + (move.draws || 0) / 2) / n) * 100 : null,
        positionN, mostPlayedUci: ranked[0]!.move, isMostPlayed: !!move && ranked[0]!.move === uci,
    };
}

const pct = (value: number) => `${Math.round(value)}%`;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export interface InsightContext {
    verdict: Verdict | null;
    bestSan?: string | null;
    db?: DbStats | null;
    mine?: MyStats | null;
    repertoire?: RepertoireContext | null;
    hooks?: { games?: boolean };
}

/**
 * Picks the one sentence shown under the verdict. Disagreements between sources come first (that is
 * what a single engine number cannot tell), then confirmations, then a hook for what the user is
 * missing, and finally a plain engine sentence. The insight never changes the verdict.
 */
export function selectInsight(ctx: InsightContext): Insight {
    const { verdict, bestSan, db, mine, repertoire, hooks = {} } = ctx;
    const solid = isSolid(verdict);
    const slip = verdict === "inaccuracy" || verdict === "mistake" || verdict === "blunder";
    const insight = (code: string, tone: InsightTone, text: string): Insight => ({ code, tone, text });

    const rep = repertoire && repertoire.available ? repertoire : null;
    const expected = rep && rep.expectedSan && rep.expectedSan.length ? rep.expectedSan[0] : null;
    const mineQuotable = !!mine && mine.n >= MINE_MIN_GAMES && mine.scorePct !== null;
    const dbQuotable = !!db && db.n >= DB_MIN_GAMES && db.scorePct !== null;
    const dbCanDisagree = dbQuotable && db!.n >= DB_MIN_GAMES_TO_DISAGREE;
    const dbPopular = !!db && db.positionN >= DB_MIN_POSITION_GAMES;

    // 1. Disagreements
    if (rep && rep.inRepertoire && slip && bestSan) {
        return insight("rep_in_engine_prefers", "warning", `In your repertoire, but the engine prefers ${bestSan}. Worth a second look.`);
    }
    if (rep && !rep.inRepertoire && expected) {
        return solid
            ? insight("rep_out_good", "neutral", `Out of repertoire, yet a good move. Your file plays ${expected}.`)
            : insight("rep_out", "warning", `Out of repertoire. Your file plays ${expected}.`);
    }
    if (mineQuotable && solid && db && mine!.scorePct! <= db.baselinePct - 10) {
        return insight("mine_scores_low", "warning", `Good move, but it hasn't worked for you yet: ${pointsLong(mine!.scorePct!)} in ${plural(mine!.n, "game")}.`);
    }
    if (mineQuotable && slip && verdict !== "blunder" && mine!.scorePct! >= 60) {
        return insight("mine_scores_high", "neutral", `The engine isn't convinced, but it works for you: ${pointsLong(mine!.scorePct!)} in ${plural(mine!.n, "game")}.`);
    }
    if (mine && slip && mine.isMostPlayed && mine.n >= 5 && bestSan) {
        return insight("mine_habit", "warning", `A habit to review: you've played this in ${plural(mine.n, "game")}. The engine prefers ${bestSan}.`);
    }
    if (dbCanDisagree && solid && db!.scorePct! <= db!.baselinePct - 4) {
        return insight("db_good_scores_low", "warning", `Good for the engine, but hard to play: players at this level take ${pointsLong(db!.scorePct!)} with it.`);
    }
    if (dbCanDisagree && slip && verdict !== "blunder" && db!.scorePct! >= db!.baselinePct + 4) {
        return insight("db_practical", "neutral", `Not the engine's favourite, but practical: players at this level take ${pointsLong(db!.scorePct!)} with it.`);
    }
    if (dbPopular && (verdict === "mistake" || verdict === "blunder") && db!.sharePct >= 15) {
        return insight("db_common_slip", "warning", `A common slip: ${pct(db!.sharePct)} of players at this level play it too.${bestSan ? ` ${bestSan} is stronger.` : ""}`);
    }
    if (dbPopular && verdict === "inaccuracy" && db!.sharePct >= 10 && bestSan) {
        return insight("db_popular_inaccuracy", "neutral", `Popular at this level (${pct(db!.sharePct)} play it), but ${bestSan} is more precise.`);
    }
    if (dbPopular && solid && db!.n > 0 && db!.sharePct < 5) {
        return insight("db_rare_sound", "positive", `Sound and rare: only ${pct(db!.sharePct)} of players choose it.`);
    }

    // 2. Confirmations
    if (rep && rep.inRepertoire) {
        return insight("rep_in", "positive", "Following repertoire.");
    }
    if (mineQuotable && solid) {
        return insight("mine_usual", "positive", `Your usual move: ${pct(mine!.scorePct!)} in ${plural(mine!.n, "game")}.`);
    }
    if (dbQuotable && dbPopular && solid) {
        return db!.rank === 1
            ? insight("db_main", "positive", `The main move at this level: ${pct(db!.sharePct)} play it, scoring ${pct(db!.scorePct!)}.`)
            : insight("db_known", "positive", `Played by ${pct(db!.sharePct)} at this level, scoring ${pct(db!.scorePct!)}.`);
    }

    // 3. Hook: what an account would add
    if (hooks.games && verdict) {
        return insight("hook_games", "hook", "How does this move work for you? Import your games to find out.");
    }

    // 4. Engine only
    const best = bestSan || "the main line";
    const engineText = verdict ? {
        best: "The engine's first choice.",
        good: `Solid. The engine slightly prefers ${best}.`,
        inaccuracy: `Playable, but ${best} was more precise.`,
        mistake: `This gives some ground away. ${best} was the move.`,
        blunder: `A big slip. Take it back and look for ${best}.`,
    }[verdict] : null;
    if (engineText) {
        return insight(`engine_${verdict}`, solid ? "positive" : "warning", engineText);
    }
    return insight("none", "neutral", "");
}
